/**
 * PdfPreviewPopover — Preview PDF au survol de l'icône fichier.
 *
 * Design senior : hover 400ms → charge le PDF dans un iframe, positionné
 * à droite avec un léger décalage. Ferme au mouseleave. Le popover ne
 * bloque pas le clic (l'icône reste cliquable pour ouvrir le PDF en full).
 */
import { useEffect, useRef, useState } from "react";
import { FileText, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PdfPreviewPopover({ fileAssetId, label = "PDF", testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const timerRef = useRef(null);

  const url = fileAssetId ? `${API}/files/${fileAssetId}` : null;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const scheduleOpen = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) {
        const w = 360, h = 460;
        const overflowsRight = r.right + w + 12 > window.innerWidth;
        setPos({
          top: Math.max(12, Math.min(window.innerHeight - h - 12, r.top - 20)),
          left: overflowsRight ? r.left - w - 12 : r.right + 12,
        });
        setOpen(true);
      }
    }, 350);
  };
  const scheduleClose = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 150);
  };

  if (!fileAssetId) return null;

  return (
    <>
      <a
        ref={anchorRef}
        href={url}
        target="_blank"
        rel="noreferrer"
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800"
        title={`Voir le ${label}`}
        data-testid={testId}
      >
        <FileText className="h-3.5 w-3.5" strokeWidth={2} />
      </a>
      {open && (
        <div
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-[360px] h-[460px] rounded-xl border border-slate-200 shadow-xl bg-white overflow-hidden"
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          data-testid="pdf-preview-popover"
        >
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="text-[11px] font-medium text-slate-600 truncate">Aperçu {label}</span>
            <a href={url} target="_blank" rel="noreferrer"
               className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
              Ouvrir <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <iframe
            src={`${url}#toolbar=0&navpanes=0&view=FitH`}
            title="Aperçu PDF"
            className="w-full h-[calc(100%-32px)] border-0"
            loading="lazy"
          />
        </div>
      )}
    </>
  );
}
